<template>
  <div>
    <v-col cols="12" class="px-6">
      <PayOffMortgage :form-input="v$.mortgage" v-model="mortgage" @input="onInput" />
      <PayOffDebt :form-input="v$.debt" v-model="debt" @input="onInput" />
      <FundEducation :form-input="v$.education" v-model="education" @input="onInput" />
      <CoverFuneralExpenses :form-input="v$.expenses" v-model="expenses" @input="onInput" />
    </v-col>
    <v-col cols="12" class="px-6">
      <v-row class="d-flex align-center">
        <v-col cols="12" sm="8" md="9" class="mt-1" :class="$vuetify.display.xs ? 'pb-0' : 'pb-4'">
          <h4 id="immediate_cash_needs_ada">
            {{ $t('L_add_spouse') }}
          </h4>
        </v-col>
        <v-col cols="12" sm="4" md="3" class="mt-0" :class="{ 'pl-3': $vuetify.display.smAndUp }">
          <v-switch
            aria-labelledby="immediate_cash_needs_ada"
            v-model="spouseIncluded"
            inset
            :label="spouseIncluded ? $t('L_yes_only') : $t('L_no_only')"
            hide-details
            @update:model-value="$emit('include-spouse', $event)"
          ></v-switch>
        </v-col>
      </v-row>
    </v-col>
    <v-col cols="12" v-if="includeSpouse" class="spouse px-6 pb-6">
      <div class="d-flex align-center ga-4 mb-8">
        <v-img
          src="@/modules/policy/PolicyGuidanceCalculator/assets/immediate.svg"
          alt="immediate cash need"
          role="presentation"
          width="40"
          max-width="40"
          min-width="40"
        />
        <h2 class="text-h2">
          {{ $t('L_spouse_immediate_needs') }}
        </h2>
      </div>
      <PayOffMortgage :form-input="v$.spouseMortgage" v-model="spouseMortgage" @input="onInput" />
      <PayOffDebt :form-input="v$.spouseDebt" v-model="spouseDebt" @input="onInput" />
      <FundEducation :form-input="v$.spouseEducation" v-model="spouseEducation" @input="onInput" />
      <CoverFuneralExpenses
        :form-input="v$.spouseExpenses"
        v-model="spouseExpenses"
        @input="onInput"
      />
    </v-col>
  </div>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { mapState, mapStores } from 'pinia';
import { useDimeguidanceStore } from '@/modules/dimeguidance/store/dimeguidance.store';
import PayOffMortgage from './ImmediateCashNeedsPayOffMortgage.vue';
import PayOffDebt from './ImmediateCashNeedsPayOffDebt.vue';
import CoverFuneralExpenses from './ImmediateCashNeedsCoverFuneralExpenses.vue';
import FundEducation from './ImmediateCashNeedsFundEducation.vue';
import { useVuelidate } from '@vuelidate/core';

const componentOptions: ComponentOptions = {
  name: 'ImmediateCashNeeds',
  components: {
    FundEducation,
    CoverFuneralExpenses,
    PayOffDebt,
    PayOffMortgage,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    dimeData: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  data: () => ({
    spouse: false,
    mortgage: 0,
    debt: 0,
    expenses: 0,
    education: 0,
    spouseMortgage: 0,
    spouseDebt: 0,
    spouseExpenses: 0,
    spouseEducation: 0,
    spouseIncluded: false,
    panels: [true, true],
    v$: useVuelidate(),
  }),
  validations: {
    mortgage: {},
    debt: {},
    expenses: {},
    education: {},
    spouseMortgage: {},
    spouseDebt: {},
    spouseExpenses: {},
    spouseEducation: {},
  },
  computed: {
    ...mapStores(useDimeguidanceStore),
    ...mapState(useDimeguidanceStore, {
      includeSpouse: 'includeSpouse',
    }),
  },
  methods: {
    onInput() {
      this.$emit('updated', {
        you: {
          mortgage: +this.mortgage,
          debt: +this.debt,
          education: +this.education,
          funeral: +this.expenses,
        },
        spouse: {
          mortgage: +this.spouseMortgage,
          debt: +this.spouseDebt,
          education: +this.spouseEducation,
          funeral: +this.spouseExpenses,
        },
      });
    },
  },
  mounted() {
    this.mortgage = this.v$.mortgage.$model = this.dimeData.you.mortgage;
    this.debt = this.v$.debt.$model = this.dimeData.you.debt;
    this.education = this.v$.education.$model = this.dimeData.you.education;
    this.expenses = this.v$.expenses.$model = this.dimeData.you.funeral;
    this.spouseMortgage = this.v$.spouseMortgage.$model = this.dimeData.spouse.mortgage;
    this.spouseDebt = this.v$.spouseDebt.$model = this.dimeData.spouse.debt;
    this.spouseEducation = this.v$.spouseEducation.$model = this.dimeData.spouse.education;
    this.spouseExpenses = this.v$.spouseExpenses.$model = this.dimeData.spouse.funeral;
    this.spouseIncluded = !!this.includeSpouse;
  },
  watch: {
    dimeData(value) {
      this.spouseMortgage = this.v$.spouseMortgage.$model = `${value?.spouse?.mortgage}`;
      this.spouseDebt = this.v$.spouseDebt.$model = `${value?.spouse?.debt}`;
      this.spouseEducation = this.v$.spouseEducation.$model = `${value?.spouse?.education}`;
      this.spouseExpenses = this.v$.spouseExpenses.$model = `${value?.spouse?.funeral}`;
    },
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
@media only screen and (max-width: 399px) {
  .pl-5.pr-4 {
    padding-left: 20px !important;
    padding-right: 20px !important;
  }
}
.spouse {
  background-color: #e0e0e0 !important;
  border-top: none !important;

  .v-expansion-panel__header {
    border-top: none !important;
  }
}
// .expansion-panel-header {
//   @media only screen and (max-width: 399px) {
//     padding: 16px !important;
//     font-size: 18px !important;

//     img.header-icon {
//       height: 2em;
//     }
//   }
// }
</style>
